import { Box, Container, Paper, Stack, Typography } from "@mui/material";
import SectionReveal from "../components/SectionReveal";

const NAVY = "#102B63";
const SOFT_TEXT = "#4A5E84";
const CTA_ORANGE = "#F5A623";

const useCases = [
  "Accélération d’un projet en cours",
  "Remplacement ou montée en charge rapide",
  "Accès à des compétences spécifiques",
];

const expertiseList = [
  "Développement Web & Mobile",
  "QA & Testing",
  "DevOps & Cloud",
  "Data & Analytics",
  "Infrastructure & Support",
  "ERP & Applications métiers",
];

const interventionList = [
  "Cadrage des besoins et des objectifs",
  "Pilotage du delivery",
  "Coordination des équipes",
  "Suivi des performances et amélioration continue",
];

const valueList = [
  "Meilleure maîtrise des risques",
  "Gain de visibilité sur l’avancement",
  "Amélioration de la qualité du delivery",
];

const technologies = [
  { name: "", src: "/assets/technologies/symfony.png" },
  { name: "", src: "/assets/technologies/php.png" },
  { name: "", src: "/assets/technologies/python.png" },
  { name: "", src: "/assets/technologies/react.png" },
  { name: "", src: "/assets/technologies/nodejs.png" },
  { name: "", src: "/assets/technologies/dotnet.png" },
  { name: "", src: "/assets/technologies/java.png" },
  { name: " ", src: "/assets/technologies/springboot.png" },
  { name: "", src: "/assets/technologies/postgresql.png" },
  { name: "", src: "/assets/technologies/mongodb.png" },
  { name: "", src: "/assets/technologies/sonarqube.png" },
  { name: "", src: "/assets/technologies/gitlab.png" },
  { name: "", src: "/assets/technologies/llama.png" },
  { name: "", src: "/assets/technologies/qwen.png" },
  { name: "", src: "/assets/technologies/mistral.png" },
];

function InfoCard({ title, children, minHeight }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: { xs: 3.5, md: 4 },
        bgcolor: "rgba(255,255,255,0.98)",
        border: "1px solid rgba(16,43,99,0.08)",
        boxShadow: "0 14px 34px rgba(16,43,99,0.05)",
        minHeight: minHeight || "auto",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: { xs: 3, md: 3.6 } }}>
        <Box
          sx={{
            px: { xs: 0.35, md: 0.8 },
            pt: { xs: 0.4, md: 0.6 },
          }}
        >
          <Typography
            sx={{
              color: NAVY,
              fontWeight: 800,
              mb: 1.5,
              lineHeight: 1.3,
              fontSize: { xs: "1rem", md: "1.08rem" },
              maxWidth: "92%",
            }}
          >
            {title}
          </Typography>
          {children}
        </Box>
      </Box>
    </Paper>
  );
}

function BulletList({ items }) {
  return (
    <Stack spacing={1.1}>
      {items.map((item) => (
        <Typography
          key={item}
          sx={{
            color: SOFT_TEXT,
            lineHeight: 1.85,
            fontSize: { xs: "0.97rem", md: "1rem" },
            maxWidth: "94%",
            pr: { xs: 0.2, md: 0.4 },
          }}
        >
          • {item}
        </Typography>
      ))}
    </Stack>
  );
}

export default function QuiSommesNousPage() {
  return (
    <Box sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 7, md: 9 } }}>
      <Container maxWidth="xl">
        <SectionReveal>
          <Paper
            sx={{
              p: { xs: 3.1, md: 5.2 },
              borderRadius: { xs: 4.5, md: 5 },
              background:
                "linear-gradient(135deg, #FFFFFF 0%, #F6F8FD 50%, #EEF3FB 100%)",
              border: "1px solid rgba(16,43,99,0.08)",
              boxShadow: "0 18px 45px rgba(16,43,99,0.08)",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -60,
                right: -60,
                width: 180,
                height: 180,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(47,78,139,0.08), transparent 70%)",
                pointerEvents: "none",
              }}
            />

            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                sx={{
                  color: CTA_ORANGE,
                  fontWeight: 800,
                  mb: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  fontSize: { xs: "0.78rem", md: "0.86rem" },
                }}
              >
                Qui sommes-nous
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  color: NAVY,
                  mb: 2.1,
                  fontWeight: 800,
                  lineHeight: 1.08,
                  letterSpacing: "-0.03em",
                  maxWidth: 980,
                  fontSize: { xs: "1.95rem", md: "2.7rem" },
                }}
              >
                Un partenaire opérationnel, au cœur de vos enjeux critiques
              </Typography>

              <Typography
                sx={{
                  color: SOFT_TEXT,
                  lineHeight: 1.92,
                  maxWidth: 980,
                  mb: 4,
                  fontSize: { xs: "1rem", md: "1.04rem" },
                }}
              >
                Axiontek intervient là où les projets IT exigent rapidité
                d’exécution, exigence technique et compréhension des enjeux
                métiers.
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                  gap: { xs: 2, md: 2.4 },
                  mb: 4,
                }}
              >
                <InfoCard title="Une méthodologie structurée, orientée impact" minHeight={215}>
                  <Typography
                    sx={{
                      color: SOFT_TEXT,
                      lineHeight: 1.9,
                      fontSize: { xs: "0.98rem", md: "1rem" },
                      maxWidth: "94%",
                      pr: { xs: 0.2, md: 0.4 },
                    }}
                  >
                    Comprendre : analyser les enjeux métiers et techniques.
                    Construire : mobiliser rapidement les expertises.
                    Pérenniser : garantir la performance et l’évolution.
                  </Typography>
                </InfoCard>

                <InfoCard title="Notre différence" minHeight={215}>
                  <Typography
                    sx={{
                      color: SOFT_TEXT,
                      lineHeight: 1.9,
                      fontSize: { xs: "0.98rem", md: "1rem" },
                      maxWidth: "94%",
                      pr: { xs: 0.2, md: 0.4 },
                    }}
                  >
                    Réactivité, experts opérationnels, approche pragmatique et
                    relation de confiance.
                  </Typography>
                </InfoCard>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                  gap: { xs: 2, md: 2.4 },
                  mb: 4,
                }}
              >
                <InfoCard title="Cas d’usage" minHeight={285}>
                  <BulletList items={useCases} />
                </InfoCard>

                <InfoCard title="Expertises disponibles" minHeight={285}>
                  <BulletList items={expertiseList} />
                </InfoCard>

                <InfoCard title="Valeur apportée" minHeight={285}>
                  <BulletList items={valueList} />
                </InfoCard>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: { xs: 3.5, md: 4 },
                  bgcolor: "rgba(255,255,255,0.98)",
                  border: "1px solid rgba(16,43,99,0.08)",
                  boxShadow: "0 14px 34px rgba(16,43,99,0.05)",
                  overflow: "hidden",
                  mb: 4,
                }}
              >
                <Box sx={{ p: { xs: 3, md: 3.6 } }}>
                  <Box
                    sx={{
                      px: { xs: 0.35, md: 0.8 },
                      pt: { xs: 0.4, md: 0.6 },
                    }}
                  >
                    <Typography
                      sx={{
                        color: NAVY,
                        fontWeight: 800,
                        mb: 1.5,
                        lineHeight: 1.3,
                        fontSize: { xs: "1rem", md: "1.08rem" },
                      }}
                    >
                      Technologies maîtrisées
                    </Typography>

                    <Typography
                      sx={{
                        color: SOFT_TEXT,
                        lineHeight: 1.9,
                        mb: 2.4,
                        maxWidth: "94%",
                        fontSize: { xs: "0.98rem", md: "1rem" },
                        pr: { xs: 0.2, md: 0.4 },
                      }}
                    >
                      Nous intervenons avec des technologies reconnues et éprouvées,
                      adaptées aux enjeux de développement, d’intégration, de
                      qualité, de déploiement et d’industrialisation.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(2, minmax(0, 1fr))",
                        sm: "repeat(3, minmax(0, 1fr))",
                        md: "repeat(4, minmax(0, 1fr))",
                        xl: "repeat(5, minmax(0, 1fr))",
                      },
                      gap: { xs: 1.4, md: 1.7 },
                    }}
                  >
                    {technologies.map((tech) => (
                      <Paper
                        key={tech.name}
                        elevation={0}
                        sx={{
                          p: { xs: 1.7, md: 1.9 },
                          borderRadius: 3,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          gap: 1,
                          minHeight: { xs: 136, md: 146 },
                          bgcolor: "rgba(244,247,252,0.96)",
                          border: "1px solid rgba(16,43,99,0.06)",
                          boxShadow: "none",
                        }}
                      >
                        <Box
                          component="img"
                          src={tech.src}
                          alt={tech.name}
                          sx={{
                            height: { xs: 34, md: 40 },
                            width: "auto",
                            maxWidth: "100%",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                        <Typography
                          sx={{
                            color: NAVY,
                            fontSize: { xs: "0.88rem", md: "0.93rem" },
                            fontWeight: 700,
                            textAlign: "center",
                            lineHeight: 1.35,
                            px: 0.4,
                          }}
                        >
                          {tech.name}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              </Paper>

              <InfoCard title="Notre intervention">
                <BulletList items={interventionList} />
              </InfoCard>
            </Box>
          </Paper>
        </SectionReveal>
      </Container>
    </Box>
  );
}